import { ArrowDownTrayIcon, EllipsisHorizontalIcon, PlusIcon } from "@heroicons/react/20/solid";
import { type AuditObjectAuditSummary, type DriverActivity, formatOtsDateTime } from "@ots-frontend/ots-data";
import { type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAppDensity } from "../app/AppContext";
import { MetricStrip } from "../components/MetricStrip";
import { OtsDayStepper } from "../components/OtsDayStepper";
import { OtsActionButton, OtsIconActionButton } from "../components/OtsButton";
import { OtsInlineAlert } from "../components/OtsInlineAlert";
import { type OtsFloatingAnchor } from "../components/OtsFloatingPanel";
import { OtsToolsMenu, OtsToolsMenuButton } from "../components/OtsToolsMenu";
import { OtsSelectionBar } from "../components/OtsSelectionBar";
import { OtsTableLoader } from "../components/OtsTableLoader";
import { type OtsBrand, type OtsColorSchemePreference, OtsThemeRoot } from "../theme/OtsTheme";
import { ActivityAttachmentsDialog } from "./ActivityAttachmentsDialog";
import { TicketAuditDialog } from "../audit/TicketAuditDialog";
import { downloadDriverActivityCsv } from "./driver-activity-export";
import { activityActionIcon, DRIVER_ACTIVITY_INSPECTOR_ACTIONS } from "./driver-activity-action-registry";
import { buildActivityToolGroups, isActivityActionEnabled, isActivityActionVisible } from "./driver-activity-tools";
import { readWorkspaceSession, writeWorkspaceSession } from "./workspace/workspace-session";
import { DriverActivityFilters } from "./DriverActivityFilters";
import { OtsStaleDataNotice } from "../components/OtsStaleDataNotice";
import { OtsDialog } from "../components/OtsDialog";
import { limitFacets, type ActivityDriver, type ActivityFacetKey, type ActivityFacets, type ActivityRateModelOption } from "./driver-activity-view-model";
import { formatActivityMoney, formatActivityQuantity } from "./driver-activity-format";
import {
  type ActivityAttachment,
  type ActivityEntry,
  type ActivityTransportEdit,
  type ActivityViewFilter,
  isActivityTicket,
} from "./driver-activity-view-model";
import { buildDayMetrics } from "./workspace/driver-activity-metrics";
import { useActivityDay } from "./workspace/useActivityDay";
import { activityKeyboardHandler } from "./workspace/useActivityKeyboard";
import { useActivitySelection } from "./workspace/useActivitySelection";
import { useDayChangeLoader } from "./workspace/useDayChangeLoader";
import { toggled } from "./workspace/workspace-utils";
import { DriverActivityInspector } from "./DriverActivityInspector";
import { DriverActivityList, type DriverActivityListHandle } from "./DriverActivityList";
import {
  DEFAULT_DRIVER_ACTIVITY_DISPLAY,
  type DriverActivityDisplayOptions,
  DriverActivityToolbar,
} from "./DriverActivityToolbar";
import { DRIVER_ACTIVITY_ACTIONS, type DriverActivityAction, type DriverActivityActionProvider } from "./driver-activity-actions";


/** The parts of the view worth putting in a URL: what is searched, filtered, and open. */
export type DriverActivityViewState = {
  query: string;
  view: ActivityViewFilter;
  /** Key of the row open in the inspector. */
  activeKey?: string;
  /** Drivers, equipment, and companies the day is narrowed to. */
  facets?: ActivityFacets;
};

export type DriverActivityWorkspaceProps = {
  /** The `/driver-activity/search` result for the day, in server order. */
  rows?: DriverActivity[];
  /** Latest audit summaries for the day's tickets (`POST /ttas/audit/summary`). Audit rings appear once given. */
  audits?: AuditObjectAuditSummary[];
  date: Date;
  onDateChange?: (date: Date) => void;
  /** IANA zone the times are shown in; defaults to the browser's. */
  timeZone?: string;
  title?: ReactNode;
  isLoading?: boolean;
  isRefreshing?: boolean;
  error?: ReactNode;
  onRetry?: () => void;
  onRefresh?: () => void;
  /** Hides the actions that change data. */
  readOnly?: boolean;
  showRevenue?: boolean;
  /**
   * Rate models to offer beside those on the day's tickets, such as the region's. Revenue, cost, and shift
   * costing count the hauling ones until others are picked in the display options.
   */
  rateModelOptions?: ActivityRateModelOption[];
  /** The kinds of filter offered; all of them by default. Picks of other kinds in `initialView` are dropped. */
  facetKeys?: readonly ActivityFacetKey[];
  actions?: DriverActivityAction[];
  /** Without a handler no actions are offered. */
  onAction?: (actionId: string, entries: ActivityEntry[]) => void;
  /**
   * Actions with their own handling and dialogs (see `combineActionProviders`); used instead of `actions`
   * and `onAction`. Its dialogs render inside the workspace, so they share its theme.
   */
  actionProvider?: DriverActivityActionProvider;
  /** A ticket's full audit, for the inspector; hosts usually return `ConnectedTicketAuditPanel`. */
  renderAuditDetails?: (entry: ActivityEntry) => ReactNode;
  /** A map of a ticket's route, for the inspector; hosts usually return `ActivityRouteMap` with their Maps key. */
  renderMap?: (entry: ActivityEntry) => ReactNode;
  /**
   * A driver's whole day on a map, from the map button in their header; see `ConnectedActivityDayMap`. `openEntry`
   * opens one of the day's rows in the inspector.
   */
  renderDriverMap?: (driver: ActivityDriver, openEntry: (entryKey: string) => void) => ReactNode;
  /** The actions the inspector shows as icons, in order; the rest go in its More menu. */
  inspectorActionIds?: readonly string[];
  /**
   * Remembers each day's scroll position, ticked tickets, and folded drivers for this browser session, so leaving
   * for a ticket or shift form and coming back finds them as they were. Give one per day and region, such as
   * `${regionId}:${day}`; without it nothing is remembered.
   */
  sessionKey?: string;
  /** Minutes after loading before the day says it may be out of date; it doesn't update live. 0 never says so. */
  staleAfterMinutes?: number;
  /**
   * Saves a ticket number or quantity corrected in place. Reject the promise to show why it could not be
   * saved. Cells are read-only without it, and whenever the workspace is `readOnly`.
   */
  onEditTransport?: (entry: ActivityEntry, change: ActivityTransportEdit) => void | Promise<unknown>;
  /**
   * Downloads attachments: one file, a ticket's set, or those of every selected ticket. The host resolves
   * the signed links (`AttachmentFile.downloadUrl`, `AttachmentBundle.bulkDownloadUrl`). Download controls
   * are hidden without it.
   */
  onDownloadAttachments?: (attachments: ActivityAttachment[], entries: ActivityEntry[]) => void;
  initialDisplay?: DriverActivityDisplayOptions;
  onDisplayChange?: (display: DriverActivityDisplayOptions) => void;
  /** Where to start, e.g. from a shared link. Read once. */
  initialView?: Partial<DriverActivityViewState>;
  /** Called as the search, quick view, or open row changes, so a host can keep it in the URL. */
  onViewChange?: (view: DriverActivityViewState) => void;
  /** When the rows were last loaded. */
  lastUpdated?: number;
  /** Name of the CSV the export button saves; the button is hidden without it. */
  exportFileName?: string;
  /** "Now" for open shifts and the Today shortcut; injectable for tests and stories. */
  now?: number;
  /** WaterGo's `scadaHighPercentage` and `scadaLowPercentage` deployment settings; 5 and -5 when missing. */
  scadaHighPercent?: number;
  scadaLowPercent?: number;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  density?: "compact" | "default";
  className?: string;
};

/** "WG-1002 · P-48219 → D-90127 · Maria Torres": which ticket the audit dialog is about. */
const ticketAuditSubtitle = (entry: ActivityEntry) => [
  entry.resourceId,
  [entry.pickup?.ticketNumber, entry.delivery?.ticketNumber].filter(Boolean).join(" → "),
  entry.pickup?.personName ?? entry.delivery?.personName,
].filter(Boolean).join(" · ");

/** Actions the inspector offers in place rather than in its action row: gauge audits sit beside the gauge quantities. */
const INSPECTOR_INLINE = /^gauge-audit-/;

const LOADER_DESCRIPTION = "Shifts, tickets, and geofence visits";

/**
 * A day of driver activity: the React counterpart of the WaterGo Driver Activity viewer.
 * Presentational — the host fetches the rows and performs the actions.
 */
export const DriverActivityWorkspace = ({
  rows,
  audits,
  date,
  onDateChange,
  timeZone,
  title = "Driver Activity",
  isLoading = false,
  isRefreshing = false,
  error,
  onRetry,
  onRefresh,
  readOnly = false,
  showRevenue = true,
  facetKeys,
  rateModelOptions,
  actions: actionsProp = DRIVER_ACTIVITY_ACTIONS,
  onAction: onActionProp,
  actionProvider,
  renderAuditDetails,
  renderMap,
  renderDriverMap,
  onEditTransport,
  onDownloadAttachments,
  initialDisplay = DEFAULT_DRIVER_ACTIVITY_DISPLAY,
  onDisplayChange,
  initialView,
  onViewChange,
  lastUpdated,
  exportFileName,
  now: nowProp,
  scadaHighPercent,
  scadaLowPercent,
  brand,
  colorScheme,
  density: densityProp,
  inspectorActionIds = DRIVER_ACTIVITY_INSPECTOR_ACTIONS,
  sessionKey,
  staleAfterMinutes = 10,
  className,
}: DriverActivityWorkspaceProps) => {
  const density = useAppDensity(densityProp);
  const rowIdPrefix = `${useId()}-row-`;
  const [query, setQuery] = useState(initialView?.query ?? "");
  const [view, setView] = useState<ActivityViewFilter>(initialView?.view ?? "all");
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<DriverActivityListHandle>(null);
  const getScrollElement = useCallback(() => scrollRef.current, []);
  const [display, setDisplay] = useState(initialDisplay);
  // Folded drivers belong to a day: another day swaps in its own during the render that changes it.
  const [collapsedState, setCollapsedState] = useState<{ key?: string; drivers: ReadonlySet<string> }>(
    () => ({ key: sessionKey, drivers: new Set(readWorkspaceSession(sessionKey).collapsed ?? []) }),
  );
  let collapsedCurrent = collapsedState;
  if (collapsedState.key !== sessionKey) {
    collapsedCurrent = { key: sessionKey, drivers: new Set(readWorkspaceSession(sessionKey).collapsed ?? []) };
    setCollapsedState(collapsedCurrent);
  }
  const collapsed = collapsedCurrent.drivers;
  const setCollapsed = useCallback((update: ReadonlySet<string> | ((previous: ReadonlySet<string>) => ReadonlySet<string>)) =>
    setCollapsedState((previous) => ({ key: previous.key, drivers: typeof update === "function" ? update(previous.drivers) : update })), []);
  // Where the tools menu was opened from, and the tickets it acts on.
  const [toolsMenu, setToolsMenu] = useState<{ anchor: OtsFloatingAnchor; targets: ActivityEntry[] }>();
  const [activeKey, setActiveKey] = useState(initialView?.activeKey);
  const [facets, setFacets] = useState<ActivityFacets>(() => limitFacets(initialView?.facets, facetKeys));
  const [attachmentsView, setAttachmentsView] = useState<{ key: string; index: number }>();
  const [auditKey, setAuditKey] = useState<string>();
  const [driverMapKey, setDriverMapKey] = useState<string>();

  const reportView = useRef(onViewChange);
  reportView.current = onViewChange;
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) firstRender.current = false;
    else reportView.current?.({ query, view, activeKey, facets });
  }, [query, view, activeKey, facets]);
  // One "now" per data load keeps open-shift durations from changing on unrelated re-renders.
  const now = useMemo(() => nowProp ?? Date.now(), [nowProp, rows]);

  const { visible, stats, entries, navigable, facetOptions, hasRows, rateModelOptions: rateModelChoices, countedRateModels, rateModelsNarrowed } = useActivityDay({
    rows, audits, display, query, view, facets, collapsed, now, scadaHighPercent, scadaLowPercent, rateModelOptions,
  });
  const { selected, selectedEntries, toggleSelected, selectEntries, clearSelection, selectOnly } = useActivitySelection(
    entries, navigable, sessionKey ? { key: sessionKey, initial: (key) => readWorkspaceSession(key).selected } : undefined,
  );
  const changingDay = useDayChangeLoader({ date, isLoading, isRefreshing, hasRows, failed: error != null });

  useEffect(() => writeWorkspaceSession(sessionKey, { selected: [...selected] }), [sessionKey, selected]);
  useEffect(() => writeWorkspaceSession(sessionKey, { collapsed: [...collapsed] }), [sessionKey, collapsed]);
  // The day's saved scroll position goes back once its rows are on screen, not while the last day's still show.
  const pendingScroll = useRef<number | undefined>(undefined);
  useLayoutEffect(() => {
    pendingScroll.current = readWorkspaceSession(sessionKey).scrollTop;
  }, [sessionKey]);
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (pendingScroll.current == null || !element || !hasRows || changingDay || isLoading) return;
    element.scrollTop = pendingScroll.current;
    pendingScroll.current = undefined;
  });
  const scrollSave = useRef(0);
  const saveScroll = () => {
    if (!sessionKey || changingDay || pendingScroll.current != null || typeof requestAnimationFrame !== "function") return;
    cancelAnimationFrame(scrollSave.current);
    scrollSave.current = requestAnimationFrame(() => writeWorkspaceSession(sessionKey, { scrollTop: scrollRef.current?.scrollTop ?? 0 }));
  };

  const attachmentsEntry = useMemo(() => entries.find((entry) => entry.key === attachmentsView?.key), [entries, attachmentsView?.key]);
  const driverMap = useMemo(() => visible.find((driver) => driver.key === driverMapKey) ?? undefined, [visible, driverMapKey]);
  const auditEntry = useMemo(() => entries.find((entry) => entry.key === auditKey), [entries, auditKey]);
  const openAudit = useCallback((entry: ActivityEntry) => setAuditKey(entry.key), []);
  const selectedAttachments = useMemo(() => selectedEntries.flatMap((entry) => entry.attachments), [selectedEntries]);
  const openAttachments = useCallback((entry: ActivityEntry, index = 0) => setAttachmentsView({ key: entry.key, index }), []);
  const activeEntry = useMemo(() => entries.find((entry) => entry.key === activeKey), [entries, activeKey]);

  const actions = actionProvider?.actions ?? actionsProp;
  const onAction = actionProvider
    ? (actionId: string, targets: ActivityEntry[]) => { actionProvider.handleAction(actionId, targets); }
    : onActionProp;
  const available = useMemo(
    () => onAction ? actions.filter((action) => !readOnly || action.readOnly) : [],
    [actions, onAction, readOnly]
  );
  const allCollapsed = visible.length > 0 && visible.every((driver) => collapsed.has(driver.key));

  const activate = useCallback((entry: ActivityEntry) => setActiveKey((current) => current === entry.key ? undefined : entry.key), []);
  const rowElement = (entry: ActivityEntry) => document.getElementById(`${rowIdPrefix}${entry.key}`);
  const revealEntry = (entry: ActivityEntry) => {
    const row = rowElement(entry);
    if (row) return row.scrollIntoView?.({ block: "nearest" });
    // Virtualized away: bring its driver's card on screen instead.
    const driver = visible.find((candidate) => candidate.shifts.some((shift) => shift.entries.includes(entry)));
    if (driver) listRef.current?.scrollToDriver(driver.key);
  };
  const activeIndex = navigable.findIndex((entry) => entry.key === activeKey);
  const step = (offset: number) => {
    const next = navigable[activeIndex + offset];
    if (!next) return;
    setActiveKey(next.key);
    revealEntry(next);
  };
  const toggleDriver = useCallback((key: string) => setCollapsed((current) => toggled(current, key)), []);

  const changeDisplay = (next: DriverActivityDisplayOptions) => {
    setDisplay(next);
    onDisplayChange?.(next);
  };

  // As in WaterGo, the menu acts on the selection when the ticket is part of it, and on the ticket alone otherwise.
  const openToolsMenu = (entry: ActivityEntry, anchor: OtsFloatingAnchor) => {
    if (!available.length) return;
    const targets = selected.has(entry.key) ? selectedEntries : [entry];
    if (!selected.has(entry.key)) selectOnly(entry);
    setToolsMenu({ anchor, targets });
  };

  const onKeyDown = activityKeyboardHandler({
    navigable, activeKey, setActiveKey, revealEntry, toggleSelected, clearSelection, rowElement, openToolsMenu,
  });
  const metrics = buildDayMetrics(stats, { showRevenue });

  const actionButton = (action: DriverActivityAction, targets: ActivityEntry[], close?: () => void) => (
    <OtsActionButton
      key={action.id}
      size="compact"
      variant={action.group === "tools" ? "text" : action.variant ?? "secondary"}
      className={action.group === "tools" ? "!justify-start" : undefined}
      disabled={!isActivityActionEnabled(action, targets)}
      onClick={() => {
        close?.();
        onAction?.(action.id, targets);
      }}>
      {action.label}
    </OtsActionButton>
  );
  // The inspector's action row: the everyday actions as icons, and the rest that apply in its More menu.
  const inspectorActionButton = (action: DriverActivityAction, entry: ActivityEntry) => {
    const icon = activityActionIcon(action);
    return icon
      ? <OtsIconActionButton
        key={action.id}
        variant="text"
        size="compact"
        icon={icon}
        label={action.label}
        disabled={!isActivityActionEnabled(action, [entry])}
        onClick={() => onAction?.(action.id, [entry])}/>
      : actionButton(action, [entry]);
  };
  const inspectorActionRow = (entry: ActivityEntry) => {
    // Gauge audits run from the Quantity section's own buttons.
    const applicable = available.filter((action) => !INSPECTOR_INLINE.test(action.id) && isActivityActionVisible(action, [entry]));
    const main = inspectorActionIds.flatMap((id) => applicable.filter((action) => action.id === id && action.group !== "create"));
    const more = applicable.filter((action) => !main.includes(action));
    if (!main.length && !more.length) return undefined;
    return <>
      {main.map((action) => inspectorActionButton(action, entry))}
      {more.length > 0 && (
        <OtsToolsMenuButton
          iconOnly
          icon={<EllipsisHorizontalIcon/>}
          label="More actions"
          layout="list"
          placement="bottom end"
          groups={buildActivityToolGroups(more, [entry])}
          onAction={(id) => onAction?.(id, [entry])}/>
      )}
    </>;
  };
  const primary = available.filter((action) => action.group !== "tools" && action.group !== "create");
  // The selection bar's Tools menu also offers what can be made from the selection.
  const tools = available.filter((action) => action.group === "tools" || action.group === "create");
  // The header's New menu: shifts and tickets, prefilled from whatever is selected, or blank without a selection.
  const creates = available.filter((action) => action.group === "create");
  const shiftActions = !readOnly ? actionProvider?.shiftActions : undefined;
  // Costing changes the money shown, so it needs the money to be shown.
  const shiftCost = !readOnly && showRevenue ? actionProvider?.shiftCost : undefined;

  return (
    <OtsThemeRoot
      brand={brand}
      colorScheme={colorScheme}
      data-density={density}
      onKeyDown={(event) => {
        // "/" jumps to search from anywhere that is not already a text field.
        if (event.key !== "/" || event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
        event.preventDefault();
        searchRef.current?.focus();
      }}
      className={["relative flex h-full min-h-0 flex-col bg-[var(--ots-background)] text-sm", className].filter(Boolean).join(" ")}>
      {/* One line, as the ticket pages: title, the day centered, the tools on the right. Narrow hosts stack them. */}
      <header className={["@container shrink-0 border-b border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]", density === "compact" ? "px-4 py-2" : "px-6 py-3"].join(" ")}>
        <div className="grid grid-cols-[minmax(0,1fr)] items-center gap-x-4 gap-y-2 @5xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <h1 className="truncate font-[family-name:var(--ots-font-display)] text-lg font-semibold leading-8">{title}</h1>
        <div role="group" aria-label="Day" className="flex justify-center">
          <OtsDayStepper size="compact" value={date} today={new Date(now)} disabled={!onDateChange} onChange={(next) => onDateChange?.(next)}/>
        </div>
        <DriverActivityToolbar
          hideDayStepper
          className="justify-end"
          date={date}
          onDateChange={onDateChange}
          today={new Date(now)}
          query={query}
          onQueryChange={setQuery}
          searchRef={searchRef}
          lastUpdated={lastUpdated}
          now={now}
          onExport={hasRows && exportFileName ? () => downloadDriverActivityCsv(visible, exportFileName, timeZone) : undefined}
          view={view}
          onViewChange={setView}
          stats={hasRows ? stats : undefined}
          display={display}
          onDisplayChange={changeDisplay}
          allCollapsed={allCollapsed}
          onToggleAll={hasRows ? () => setCollapsed(allCollapsed ? new Set() : new Set(visible.map((driver) => driver.key))) : undefined}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
          showAuditOptions={audits != null}
          rateModelOptions={showRevenue ? rateModelChoices : undefined}
          countedRateModels={countedRateModels}
          filters={hasRows && <DriverActivityFilters value={facets} onChange={setFacets} options={facetOptions} facetKeys={facetKeys}/>}
          leading={creates.length > 0 && (
            <OtsToolsMenuButton
              label="New"
              layout="list"
              icon={<PlusIcon/>}
              placement="bottom end"
              groups={buildActivityToolGroups(creates, selectedEntries)}
              onAction={(id) => onAction?.(id, selectedEntries)}/>
          )}/>
        </div>
      </header>
      {staleAfterMinutes > 0 && hasRows && (
        <OtsStaleDataNotice
          className="mx-4 mt-2 shrink-0"
          loadedAt={lastUpdated}
          staleAfterMinutes={staleAfterMinutes}
          subject="This day"
          // A fixed "now" (stories, tests) fixes the notice's clock too; it keeps its own otherwise.
          now={nowProp}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}/>
      )}

      {/* Relative so the floating selection bar sits over the list, above the totals footer. */}
      <div className="relative flex min-h-0 flex-1">
        <div
          ref={scrollRef}
          tabIndex={0}
          aria-label="Driver activity. Use the arrow keys to move between rows, Space to select, Escape to close."
          aria-busy={isLoading || isRefreshing}
          onKeyDown={onKeyDown}
          onScroll={saveScroll}
          className="min-w-0 flex-1 overflow-auto outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ots-primary)]">
          {error != null ? (
            <OtsInlineAlert
              tone="danger"
              title="Driver activity could not be loaded"
              className="m-4"
              action={onRetry && <OtsActionButton size="compact" onClick={onRetry}>Try again</OtsActionButton>}>
              {error}
            </OtsInlineAlert>
          ) : isLoading && !hasRows ? (
            <OtsTableLoader label={`Loading ${formatOtsDateTime(date, "weekday")}`} description={LOADER_DESCRIPTION}/>
          ) : !hasRows ? (
            <p className="p-10 text-center text-[var(--ots-text-muted)]">No driver activity was recorded for this day.</p>
          ) : !visible.length ? (
            <div className="p-10 text-center text-[var(--ots-text-muted)]">
              <p>Nothing matches the current search and view.</p>
              <OtsActionButton variant="text" size="compact" className="mt-2" onClick={() => { setQuery(""); setView("all"); setFacets({}); }}>
                Clear filters
              </OtsActionButton>
            </div>
          ) : (
            <DriverActivityList
              ref={listRef}
              getScrollElement={getScrollElement}
              drivers={visible}
              timeZone={timeZone}
              showRevenue={showRevenue}
              durationMode={display.durationMode}
              density={display.compactRows ? "compact" : "default"}
              showTimeline={display.showTimeline}
              showAudit={audits != null}
              auditScoreType={display.auditScoreType}
              onSelectEntries={selectEntries}
              collapsed={collapsed}
              onToggleDriver={toggleDriver}
              selected={selected}
              onToggleSelected={toggleSelected}
              activeKey={activeKey}
              onActivate={activate}
              onOpenAudit={renderAuditDetails && openAudit}
              onOpenAttachments={openAttachments}
              onEditTransport={readOnly ? undefined : onEditTransport}
              onEntryContextMenu={available.length ? (entry, point) => openToolsMenu(entry, point) : undefined}
              onOpenDriverMap={renderDriverMap && setDriverMapKey}
              shiftActionIds={shiftActions?.ids}
              onShiftAction={shiftActions?.handle}
              shiftCost={shiftCost}
              rateModelsNarrowed={rateModelsNarrowed}
              rowIdPrefix={rowIdPrefix}
              day={date}
              idLabel={display.idLabel}
              now={now}/>
          )}
        </div>

        {changingDay && (
          <OtsTableLoader variant="overlay" label={`Loading ${formatOtsDateTime(date, "weekday")}`} description={LOADER_DESCRIPTION}/>
        )}

        {activeEntry && (
          <DriverActivityInspector
            entry={activeEntry}
            timeZone={timeZone}
            showRevenue={showRevenue}
            auditScoreType={display.auditScoreType}
            onOpenAudit={activeEntry.tta && renderAuditDetails ? () => openAudit(activeEntry) : undefined}
            map={isActivityTicket(activeEntry) ? renderMap?.(activeEntry) : undefined}
            onClose={() => setActiveKey(undefined)}
            onPrevious={activeIndex > 0 ? () => step(-1) : undefined}
            onNext={activeIndex >= 0 && activeIndex < navigable.length - 1 ? () => step(1) : undefined}
            onOpenAttachment={(index) => openAttachments(activeEntry, index)}
            onDownloadAttachments={onDownloadAttachments && (() => onDownloadAttachments(activeEntry.attachments, [activeEntry]))}
            className="w-[360px] shrink-0 border-l border-[var(--ots-outline-variant)]"
            actions={isActivityTicket(activeEntry) ? inspectorActionRow(activeEntry) : undefined}
            onRunAction={isActivityTicket(activeEntry) ? (actionId) => onAction?.(actionId, [activeEntry]) : undefined}
            canRunAction={(actionId) => available.some((action) => action.id === actionId
              && isActivityActionVisible(action, [activeEntry]) && isActivityActionEnabled(action, [activeEntry]))}/>
        )}

        <OtsSelectionBar
          count={selectedEntries.length}
          noun="ticket"
          onClear={clearSelection}
          className="absolute inset-x-0 bottom-4 z-30 mx-auto w-fit max-w-[calc(100%-2rem)]"
          summary={[
            formatActivityQuantity(
              selectedEntries.reduce((sum, entry) => sum + (entry.pickup?.quantityDecimal ?? entry.delivery?.quantityDecimal ?? 0), 0),
              stats.quantityUom
            ),
            showRevenue && formatActivityMoney(selectedEntries.reduce((sum, entry) => sum + (entry.revenue ?? 0), 0)),
          ].filter(Boolean).join(" · ")}>
          {onDownloadAttachments && selectedAttachments.length > 0 && (
            <OtsActionButton size="compact" icon={<ArrowDownTrayIcon/>} onClick={() => onDownloadAttachments(selectedAttachments, selectedEntries)}>
              Attachments ({selectedAttachments.length})
            </OtsActionButton>
          )}
          {available.length > 0 && <>
            {primary.filter((action) => isActivityActionVisible(action, selectedEntries)).map((action) => actionButton(action, selectedEntries))}
            {tools.length > 0 && (
              <OtsToolsMenuButton
              compact
                layout="list"
                placement="top end"
                groups={buildActivityToolGroups(tools, selectedEntries)}
                onAction={id => {
                  const action = tools.find(action => action.id === id);
                  if (action) onAction?.(action.id, selectedEntries);
                }}/>
            )}
          </>}
        </OtsSelectionBar>
      </div>

      {hasRows && (
        <footer className={["shrink-0 border-t border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]", density === "compact" ? "px-4 py-1.5" : "px-6 py-2"].join(" ")}>
          <MetricStrip metrics={metrics} maxVisible={8} density="compact" overflowPlacement="top end" ariaLabel="Day totals"/>
        </footer>
      )}

      {toolsMenu && (
        <OtsToolsMenu
          // Same compact density as WaterGo's right-click menu.
          compact
          layout="list"
          label="Ticket tools"
          groups={buildActivityToolGroups(available, toolsMenu.targets)}
          anchor={toolsMenu.anchor}
          onAction={(id) => {
            const targets = toolsMenu.targets;
            setToolsMenu(undefined);
            onAction?.(id, targets);
          }}
          onClose={() => setToolsMenu(undefined)}/>
      )}
      <OtsDialog
        open={!!driverMap}
        onClose={() => setDriverMapKey(undefined)}
        title={driverMap ? `${driverMap.name}'s day` : ""}
        description={driverMap?.companyName}
        size="5xl"
        brand={brand}
        colorScheme={colorScheme}>
        {driverMap && renderDriverMap?.(driverMap, (entryKey) => {
          setDriverMapKey(undefined);
          setActiveKey(entryKey);
        })}
      </OtsDialog>
      <TicketAuditDialog open={!!auditEntry} subtitle={auditEntry && ticketAuditSubtitle(auditEntry)} onClose={() => setAuditKey(undefined)} brand={brand} colorScheme={colorScheme}>
        {auditEntry && renderAuditDetails?.(auditEntry)}
      </TicketAuditDialog>
      <ActivityAttachmentsDialog
        entry={attachmentsEntry}
        index={attachmentsView?.index ?? 0}
        onIndexChange={(index) => setAttachmentsView((current) => current && { ...current, index })}
        onClose={() => setAttachmentsView(undefined)}
        timeZone={timeZone}
        brand={brand}
        colorScheme={colorScheme}
        onDownload={onDownloadAttachments && ((attachments, entry) => onDownloadAttachments(attachments, [entry]))}/>
      {actionProvider?.dialogs}
    </OtsThemeRoot>
  );
};
